import { Box, BoxProps, Text, Theme, Tooltip } from '@radix-ui/themes';
import React, { PropsWithChildren } from 'react';

import classNames from 'classnames';
import styles from './TruncatedTooltip.module.css';
import { useMeasure } from '@common/hooks/useMeasure';

const extractTextFromChildren = (children: React.ReactNode): string => {
    if (typeof children === 'string' || typeof children === 'number') {
        return String(children);
    }
    if (Array.isArray(children)) {
        return children.map(extractTextFromChildren).join('');
    }
    if (React.isValidElement(children)) {
        const element = children as React.ReactElement<PropsWithChildren>;
        if (element.props.children) {
            return extractTextFromChildren(element.props.children);
        }
    }

    return '';
};

type TruncatedTooltipProps = BoxProps & {
    text?: string;
    enabled?: boolean;
};

export const TruncatedTooltip = ({
    children,
    text,
    className,
    enabled = true,
    ...props
}: TruncatedTooltipProps) => {
    const textRef = React.useRef<HTMLDivElement>(null);
    const [containerRef, { width: containerWidth }] =
        useMeasure<HTMLDivElement>();
    const [isTruncated, setIsTruncated] = React.useState(false);

    const rawTextContent = React.useMemo(() => {
        return text ?? extractTextFromChildren(children);
    }, [children, text]);

    React.useLayoutEffect(() => {
        if (textRef.current) {
            setIsTruncated(
                textRef.current.scrollWidth > textRef.current.clientWidth,
            );
        }
    }, [children, containerWidth]);

    const TextComponent = React.useMemo(
        () => (
            <Box {...props} asChild>
                <Text
                    as='div'
                    ref={textRef}
                    truncate
                    className={classNames(className)}
                >
                    {children}
                </Text>
            </Box>
        ),
        [children, className, props],
    );

    if (!enabled) return children;

    return (
        <Box ref={containerRef} className={styles.container}>
            {isTruncated && rawTextContent ? (
                <Theme radius='small' asChild>
                    <Tooltip
                        sticky='always'
                        content={rawTextContent}
                        maxWidth='400px'
                    >
                        {TextComponent}
                    </Tooltip>
                </Theme>
            ) : (
                TextComponent
            )}
        </Box>
    );
};
